import { useEffect, useState } from "react";
import ApplicationBar from "../components/LayoutComponents/AppBar";
import JobBoard from "./Member/JobBoard/JobBoard";
import UpdateUserPage from "./Member/UpdateUserPage";
import { partnersRequest } from "../remote/partnersRequest";
import { logoutRequest } from "../remote/authRequest";
import { requestCourses } from "../remote/courseRequest";
import MemberSkills from "./Member/MemberSkills/MemberSkills";
import MemberLandingPage from "./Member/MemberLandingPage";
import { requestJobProfiles } from "../remote/jobProfilesRequest"
import { proficienciesRequest } from "../remote/proficienciesRequest";
import { competenciesRequest } from "../remote/competenciesRequest";
import { requestSkillProfiles } from "../remote/skillProfileRequest";
import { requestPrograms } from "../remote/programRequests";
import { Paper, Box } from "@mui/material";
import ProfileButton from "../components/FormComponents/ProfileButton";
import { userSkillsRequest, getUserRequest } from "../remote/usersRequests";
import Background from "../components/LayoutComponents/Background";
import LoadingCircle from "../components/LayoutComponents/LoadingCircle";
import Administration from "./Administration/Administration";
import CompetencyReportingPage from "./Reporting/CompetencyReportingPage";


const styles = {
    content: {
        position: "relative",
        minWidth: '200px',
        maxWidth: '1200px',
        minHeight: '100%',
        display: "flex",
        flexDirection: "column",
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        marginY: '3rem',
        width: '100%',
        boxSizing: 'border-box',
        mt: '2rem',
        borderRadius: 6,
        backgroundColor: '#eef1ff',
    }
}

function Application({ session, handleLogout, isMobile }) {
    const pages = session?.isStaff ? ['Career Hub', 'Build Your Profile', 'Explore Opportunities', 'Report'] : ['Career Hub', 'Build Your Profile', 'Explore Opportunities'];
    const [page, setPage] = useState('Home');
    const [partners, setPartners] = useState([]);
    const [courses, setCourses] = useState([]);
    const [userDetails, setUserDetails] = useState({});
    const [userSkills, setUserSkills] = useState(undefined);
    const [jobProfiles, setJobProfiles] = useState([]);
    const [skillProfiles, setSkillProfiles] = useState([]);
    const [competencies, setCompetencies] = useState([]);
    const [proficiencies, setProficiencies] = useState([]);
    const [programs, setPrograms] = useState([])
    const [loading, setLoading] = useState(true)


    useEffect(() => {
        const currentTime = new Date().getTime();

        const isSessionExpired = session && session.expiresIn && currentTime >= session.expiresIn;

        if (page === 'Logout') {
            if (session?.userId) {
                logoutRequest(session.userId);
            }
            handleLogout();
            return;
        }

        if (isSessionExpired) {
            alert('Session has expired. Redirecting to login.');
            logoutRequest(session.userId);
            handleLogout();
        }
    }, [session, page, handleLogout]);    

    useEffect(() => {
        if (!session?.token) return;
        let isMounted = true;
        setLoading(true);

        const fetchAll = async () => {
            try {
                const [
                    user,
                    skills,
                    partnersRes,
                    coursesRes,
                    jobProfilesRes,
                    skillProfileRes,
                    competenciesRes,
                    proficienciesRes,
                    programRes,
                ] = await Promise.all([
                    session?.token ? getUserRequest(session.token) : Promise.resolve({ data: {} }),
                    session?.token && session?.userId ? userSkillsRequest(session.token, session.userId) : Promise.resolve({ data: undefined }),
                    session?.token ? partnersRequest(session.token) : Promise.resolve({ data: [] }),
                    session?.token ? requestCourses(session.token) : Promise.resolve({ data: [] }),
                    session?.token ? requestJobProfiles(session.token) : Promise.resolve({ data: [] }),
                    session?.token ? requestSkillProfiles(session.token) : Promise.resolve({ data: [] }),
                    session?.token ? competenciesRequest(session.token) : Promise.resolve({ data: [] }),
                    session?.token ? proficienciesRequest(session.token) : Promise.resolve({ data: [] }),
                    session?.token ? requestPrograms(session.token) : Promise.resolve({ data: [] })
                ]);
                if (!isMounted) return;
                setUserDetails(user.data);
                setUserSkills(
                    skills.data
                        ?.sort((a, b) => Number(b.proficiencyRating) - Number(a.proficiencyRating))
                        ?.filter(
                            (skill, index, arr) =>
                                arr.findIndex(s => s.competencyId === skill.competencyId) === index
                        )
                );
                setPartners(partnersRes?.data);
                setCourses(coursesRes?.data);
                setJobProfiles(jobProfilesRes?.data);
                setCompetencies(competenciesRes?.data);
                setProficiencies(proficienciesRes?.data);
                setSkillProfiles(skillProfileRes?.data);
                setPrograms(programRes?.data)
            } catch (error) {
                // You can add more granular error handling here if needed
                console.error('Error fetching application data:', error);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchAll();
        return () => { isMounted = false; };
    }, [session?.token, session?.userId]);




    const handleUserSkillsUpdate = (skills) => {
        setUserSkills(skills);
    };

    const handlePageClick = (page) => {
        setPage(page);
    };

    let content;
    if (loading) {
        content = (
            <LoadingCircle />
        );
    } else {
        switch (page) {
            case 'Explore Opportunities':
                content = (
                    <JobBoard
                        session={session}
                        handlePageClick={handlePageClick}
                        userComp={userSkills}
                        city={userDetails.city}
                        state={userDetails.state}
                        courses={courses}
                        programs={programs}
                        schools={partners.filter(e => (e.entityTypeName === 'Training Provider'))}
                        isMobile={!!isMobile}
                        userDetails={userDetails}
                    />
                );
                break;
            case 'Build Your Profile':
                content = (
                    <MemberSkills
                        session={session}
                        skills={
                            userSkills.map(skill => ({
                                ...skill,
                                modified: skill.modified || false,
                                proficiencyRating: skill.proficiencyRating ?? 1,
                            }))}
                        setSkills={setUserSkills}
                        email={session?.username}
                        handleSkillsUpdate={handleUserSkillsUpdate}
                        proficiencies={proficiencies}
                        isMobile={!!isMobile}
                    />
                );
                break;
            case 'Profile':
                content = (
                    <UpdateUserPage
                        session={session}
                        profile={userDetails}
                        setProfile={setUserDetails}
                        isMobile={!!isMobile}
                    />
                );
                break;
            case 'Administration':
                content = session.role === 'Administrator' && (
                    <Administration
                        session={session}
                        competencies={competencies}
                        proficiencies={proficiencies}
                        jobProfiles={jobProfiles}
                        setJobProfiles={setJobProfiles}
                        skillProfiles={skillProfiles}
                        setSkillProfiles={setSkillProfiles}
                        courses={courses}
                        setCourses={setCourses}
                        companies={partners}
                        programs={programs}
                        setPrograms={setPrograms}
                    />
                );
                break;
            case 'Report':
                content = session.isStaff && (
                    <CompetencyReportingPage
                        session={session}
                        competencies={competencies.sort((a, b) => a.compDesc.localeCompare(b.compDesc))}
                        proficiencies={proficiencies}
                    />
                );
                break;
            default:
                content = (
                    <MemberLandingPage
                        handlePageClick={handlePageClick}
                        isMobile={isMobile}
                    />
                );
                break;
        }
    }

    return (
        <Background isMobile={isMobile}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                    minHeight: "60vh",
                }}
            >
                <ApplicationBar
                    isMobile={isMobile}
                    pages={pages}
                    handlePageClick={handlePageClick}
                    page={page}
                    Component={() => (
                        <ProfileButton
                            profile={session}
                            handlePageClick={handlePageClick}
                        />
                    )}
                />
                <Paper elevation={0} sx={styles.content}>
                    {content}
                </Paper>
            </Box>
        </Background>
    );
}

export default Application;
